import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import { useVModel } from '@vueuse/core';
import type { Maybe, OrganizationFormData, SubscriptionPlan, PlanColumn } from '../types';
import { useModal } from '../composables/modals';
import { formatCurrency } from '../composables/currencies';
import 'vue-good-table/dist/vue-good-table.css';
import { VueGoodTable } from 'vue-good-table';
import IconClose from '@/components/icons/IconClose.vue';
import SwitcherInput from '@/components/SwitcherInput.vue';
import PromocodeModal from '@/components/PromocodeModal.vue';
import IconTickCircle from '@/components/icons/IconTickCircle.vue';
import IconMinusCircle from '@/components/icons/IconMinusCirlce.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ChoosePlanTable',
  props: {
    plans: { default: () => [] },
    value: { default: undefined }
  },
  emits: ["input", "choose"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'input', payload: OrganizationFormData['coupon']): void;
    (e: 'choose', payload: OrganizationFormData['plan_id']): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { plans: SubscriptionPlan[], value: Maybe<OrganizationFormData['coupon']> };





const columns = computed<PlanColumn[]>(() => {
    const plans = props.plans.reduce<Record<string, PlanColumn>>(function (all, plan) {
        if (!all.hasOwnProperty(plan.name)) {
            all[plan.name] = {
                field: plan.name.toLowerCase(),
                label: plan.name,
                yearly: {
                    cost: '',
                    planId: '',
                    prevCost: null,
                },
                monthly: {
                    cost: '',
                    planId: '',
                    prevCost: null,
                },
            };
        }

        const monthlyPrice = plan.interval === 'monthly' ? plan.raw_price : (plan.raw_price / 12);

        all[plan.name][plan.interval]!.planId = plan.id;
        all[plan.name][plan.interval]!.cost = formatCurrency(Math.round(monthlyPrice));

        if (plan.interval === 'monthly') {
            all[plan.name].yearly!.prevCost = formatCurrency(plan.raw_price);
        }

        if (plan.trial_days) {
            all[plan.name][plan.interval]!.trialDays = plan.trial_days;
        }

        return all;
    }, {
        empty: {
            field: 'empty',
            tdClass: 'moveColumn',
        },
    });

    return Object.values(plans);
});

const items = [
    {
        isHeadRow: true,
        group: 'general',
        empty: 'General features',
        starter: 'Starter',
        professional: 'Professional',
        business: 'Business',
        enterprise: 'Enterprise',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'Launching Profile Automation',
        starter: '1',
        professional: '5',
        business: '10',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'eBay Templates',
        starter: '1',
        professional: '10',
        business: '20',
        enterprise: '20',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'Synchronizing History',
        starter: '7 Days',
        professional: '30 Days',
        business: '30 Days',
        enterprise: '30 Days',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'Users Monthly Conversations',
        starter: '25',
        professional: 'Unlimited',
        business: 'Unlimited',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'Seller Accounts',
        starter: '1',
        professional: '3',
        business: '5',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'Orders (Per Month)',
        starter: '300',
        professional: '1000',
        business: '2500',
        enterprise: 'Unlimited',
    },

    {
        isHeadRow: true,
        group: 'management',
        empty: 'Listing management',
        starter: '',
        professional: '',
        business: '',
        enterprise: '',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'Employees Accounts',
        starter: '1',
        professional: '2',
        business: '5',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'Image Hosting',
        starter: '2 MB',
        professional: '5 MB',
        business: '10 MB',
        enterprise: '50 MB',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'Email Templates',
        starter: '1',
        professional: '5',
        business: '20',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'Products',
        starter: '1000',
        professional: '5000',
        business: '20000',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'File uploading size (Excel)',
        starter: '10 MB',
        professional: '100 MB',
        business: '200 MB',
        enterprise: '500 MB',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'File Upload Limits (Rows per file)',
        starter: false,
        professional: false,
        business: false,
        enterprise: false,
    },

    {
        isHeadRow: true,
        group: 'support',
        empty: 'Support',
        starter: '',
        professional: '',
        business: '',
        enterprise: '',
    },
    {
        isHeadRow: false,
        group: 'support',
        empty: 'Chat Templates',
        starter: '1',
        professional: '3',
        business: '5',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'support',
        empty: 'Incoming Orders Frequency',
        starter: '20 Min',
        professional: '15 Min',
        business: '10 Min',
        enterprise: '5 Min',
    },
    {
        isHeadRow: false,
        group: 'support',
        empty: 'Hour Number of Help Sessions',
        starter: '2',
        professional: '5',
        business: '10',
        enterprise: '20',
    },
    {
        isHeadRow: false,
        group: 'support',
        empty: 'Form Builder',
        starter: false,
        professional: true,
        business: true,
        enterprise: true,
    },
    {
        isHeadRow: false,
        group: 'support',
        empty: 'Custom Orders',
        starter: false,
        professional: true,
        business: true,
        enterprise: true,
    },
];

function rowStyleClassFn(row: { isHeadRow: boolean; group: string }) {
    return row.isHeadRow ? 'headRow' : row.group;
}

const billingPeriod = ref<'monthly' | 'yearly'>('yearly');

function handleBillingPeriodChange(val: boolean) {
    billingPeriod.value = val ? 'yearly' : 'monthly';
}

const coupon = useVModel(props, 'value', emit, { eventName: 'input' });
const couponModal = useModal();

function handleCouponAdd(value: OrganizationFormData['coupon']) {
    coupon.value = value;
}

function handleCouponRemove() {
    coupon.value = null;
}

function handlePlanChoose(plan: OrganizationFormData['plan_id']) {
    emit('choose', plan);
}

return { __sfc: true,props, emit, columns, items, rowStyleClassFn, billingPeriod, handleBillingPeriodChange, coupon, couponModal, handleCouponAdd, handleCouponRemove, handlePlanChoose, VueGoodTable, IconClose, SwitcherInput, PromocodeModal, IconTickCircle, IconMinusCircle }
}

})